import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings, Save, FileText, UserPlus, Trash2, Mail } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FunTimeAccessPanel, LottoScanAccessPanel } from "./FunTimeAccessPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DENOMINATIONS = [1, 2, 3, 5, 10, 20, 30, 50];
const DENOM_LABELS = { 1: '$1', 2: '$2', 3: '$3', 5: '$5', 10: '$10', 20: '$20', 30: '$30', 50: '$50' };

// Feature-flag subsection -----------------------------------------------------
// Reads the raw GLOBAL flag + per-user / email grants from /admin/feature-flag-grants
// (NOT /feature-flags, which now returns effective flags with admin bypass).
const FeatureFlagsPanel = () => {
  const [data, setData] = useState({ global: {}, email_grants: {}, user_grants: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/feature-flag-grants`);
      setData(res.data || { global: {}, email_grants: {}, user_grants: [] });
    } catch {
      toast.error("Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleGlobal = async (key) => {
    const next = !data.global?.[key];
    setSaving(true);
    try {
      await axios.put(`${API}/admin/feature-flags`, { [key]: next });
      await load();
      toast.success(next ? "Visible to ALL users" : "Hidden globally — grants below still apply");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update flag");
    } finally {
      setSaving(false);
    }
  };

  const grantByEmail = async (flag) => {
    const email = (emailInput || "").trim().toLowerCase();
    if (!email) {
      toast.error("Enter an email to grant");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/feature-flag-grants`, { flag, grant: true, email });
      setEmailInput("");
      await load();
      toast.success(`Granted ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant");
    } finally {
      setSaving(false);
    }
  };

  const revokeEmail = async (flag, email) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/feature-flag-grants`, { flag, grant: false, email });
      await load();
      toast.success(`Revoked ${email}`);
    } catch (err) {
      toast.error("Failed to revoke");
    } finally {
      setSaving(false);
    }
  };

  const revokeUser = async (flag, userId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/feature-flag-grants`, { flag, grant: false, user_id: userId });
      await load();
      toast.success("Revoked");
    } catch (err) {
      toast.error("Failed to revoke");
    } finally {
      setSaving(false);
    }
  };

  const FLAG = "store_paperwork_enabled";
  const flagOn = !!data.global?.[FLAG];
  const emailList = data.email_grants?.[FLAG] || [];
  const userList = (data.user_grants || []).filter((g) => g.flags?.[FLAG]);

  return (
    <div data-testid="feature-flags-panel" className="space-y-4">
      <h3 className="font-mono font-bold text-sm">Feature Visibility</h3>

      <div className="rounded-lg border border-border/50 bg-card/50 p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-mono font-bold text-sm text-card-foreground">Store Paperwork</p>
              <p className="font-mono text-xs text-muted-foreground">
                When the global toggle is OFF, only admins and users in the allowlist below see the tab.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-muted-foreground">{flagOn ? "All users" : "Allowlist only"}</span>
            <Switch
              checked={flagOn}
              onCheckedChange={() => toggleGlobal(FLAG)}
              disabled={loading || saving}
              data-testid="toggle-store-paperwork"
            />
          </div>
        </div>

        {/* Pre-grant by email (works even if user hasn't registered yet) */}
        <div className="space-y-2 pt-3 border-t border-border/30">
          <Label className="font-mono text-xs text-muted-foreground flex items-center gap-1">
            <Mail className="w-3.5 h-3.5" /> Pre-grant by email (works before the user registers)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="user@example.com"
              className="font-mono text-xs h-8 max-w-sm"
              data-testid="feature-grant-email-input"
              onKeyDown={(e) => { if (e.key === "Enter") grantByEmail(FLAG); }}
            />
            <Button
              size="sm"
              onClick={() => grantByEmail(FLAG)}
              disabled={saving || loading}
              className="btn-3d h-8"
              data-testid="feature-grant-email-add"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Grant
            </Button>
          </div>
          {emailList.length > 0 && (
            <ul className="space-y-1 mt-2" data-testid="feature-grant-email-list">
              {emailList.map((em) => (
                <li
                  key={em}
                  className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-secondary/40 text-xs font-mono"
                >
                  <span className="truncate">{em}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => revokeEmail(FLAG, em)}
                    disabled={saving}
                    className="h-6 w-6 text-rose-400 hover:text-rose-300"
                    title="Revoke"
                    data-testid={`feature-grant-email-revoke-${em}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Per-user grants */}
        {userList.length > 0 && (
          <div className="space-y-2 pt-3 border-t border-border/30">
            <Label className="font-mono text-xs text-muted-foreground">Per-user grants</Label>
            <ul className="space-y-1" data-testid="feature-grant-user-list">
              {userList.map((g) => (
                <li
                  key={g.user_id}
                  className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-secondary/40 text-xs font-mono"
                >
                  <span className="truncate">
                    <strong>{g.name || "User"}</strong>
                    <span className="text-muted-foreground"> · {g.email}</span>
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => revokeUser(FLAG, g.user_id)}
                    disabled={saving}
                    className="h-6 w-6 text-rose-400 hover:text-rose-300"
                    title="Revoke"
                    data-testid={`feature-grant-user-revoke-${g.user_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export const SettingsTab = ({ packSizes, onPackSizesChange, onSave, saving, supportContact, onSupportContactChange, onSaveSupportContact, savingSupport }) => {
  return (
    <Card className="border-border/50" data-testid="settings-panel">
      <CardHeader>
        <CardTitle className="font-mono flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Support contact — shown in app header + login screen */}
        <div>
          <h3 className="font-mono font-bold text-sm mb-3">Support Contact (shown to users)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            <div className="space-y-1">
              <Label className="font-mono text-xs">Contact Name</Label>
              <Input
                type="text"
                value={supportContact?.name || ''}
                onChange={(e) => onSupportContactChange({ ...supportContact, name: e.target.value })}
                placeholder="Bob"
                className="font-mono text-sm"
                data-testid="support-contact-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="font-mono text-xs">Phone Number</Label>
              <Input
                type="tel"
                value={supportContact?.phone || ''}
                onChange={(e) => onSupportContactChange({ ...supportContact, phone: e.target.value })}
                placeholder="859-537-5780"
                className="font-mono text-sm"
                data-testid="support-contact-phone"
              />
            </div>
          </div>
          <Button
            onClick={onSaveSupportContact}
            disabled={savingSupport}
            className="btn-primary mt-3 flex items-center gap-2"
            data-testid="save-support-contact"
          >
            <Save className="w-4 h-4" />
            {savingSupport ? 'Saving...' : 'Save Support Contact'}
          </Button>
          <p className="text-xs text-muted-foreground mt-2 font-mono">
            Used in the app header (&quot;For help call …&quot;) and the registration screen.
          </p>
        </div>

        <hr className="border-border/30" />

        <div>
          <h3 className="font-mono font-bold text-sm mb-3">Default Pack Sizes (tickets per pack)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {DENOMINATIONS.map((d) => (
              <div key={d} className="flex items-center gap-2">
                <Label className="font-mono text-xs w-10 text-primary">{DENOM_LABELS[d]}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={packSizes[d]}
                  onChange={(e) =>
                    onPackSizesChange({ ...packSizes, [d]: e.target.value.replace(/[^0-9]/g, '') })
                  }
                  className="h-8 w-20 text-center text-xs font-mono"
                  data-testid={`pack-size-${d}`}
                />
                <span className="text-[10px] text-muted-foreground">
                  0-{(parseInt(packSizes[d]) || 1) - 1}
                </span>
              </div>
            ))}
          </div>
          <Button
            onClick={onSave}
            disabled={saving}
            className="btn-primary mt-4 flex items-center gap-2"
            data-testid="save-pack-sizes"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Pack Sizes'}
          </Button>
        </div>

        {/* Feature-flag toggles */}
        <FeatureFlagsPanel />

        {/* Fun Time access control */}
        <FunTimeAccessPanel />
        <LottoScanAccessPanel />
      </CardContent>
    </Card>
  );
};
